/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-11-05T04:17:10.268Z
 * 소스 파일: src/data/dto/time-zone-check-dto
 */

import type { TimeZoneCheckResponseDto } from "@/data/dto/time-zone-check-dto";

/**
 * TimeZoneCheckResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isTimeZoneCheckResponseDto(input: unknown): input is TimeZoneCheckResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    Array.isArray(obj.timezone) && obj.timezone.every((item) => typeof item === "object" && item !== null && !Array.isArray(item) &&
typeof (item as any).country_code === "string" &&
typeof (item as any).country_name === "string" && (item as any).country_name.trim() !== "" &&
typeof (item as any).region_name === "string" && (item as any).region_name.trim() !== "" &&
typeof (item as any).time_zone === "string") &&
    typeof obj.ip === "string"
  );
}

/**
 * TimeZoneCheckResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateTimeZoneCheckResponseDto(input: unknown): IValidation<TimeZoneCheckResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // timezone (required)
      if (objObj.timezone === undefined) {
        errors.push({ path: "timezone", expected: "array", value: undefined });
      } else {
        if (!Array.isArray(objObj.timezone)) {
          errors.push({ path: "timezone", expected: "array", value: objObj.timezone });
        } else {
          objObj.timezone.forEach((item, idx) => {
              if (typeof item !== "object" || item === null || Array.isArray(item)) {
          errors.push({ path: "timezone[idx]", expected: "object", value: item });
        } else {
          const itemObj = item as Record<string, unknown>;
          // country_code (required)
          if (itemObj.country_code === undefined) {
            errors.push({ path: "timezone[idx].country_code", expected: "string", value: undefined });
          } else {
            if (typeof itemObj.country_code !== "string") {
              errors.push({ path: "timezone[idx].country_code", expected: "string", value: itemObj.country_code });
            }
          }
          // country_name (required)
          if (itemObj.country_name === undefined) {
            errors.push({ path: "timezone[idx].country_name", expected: "string", value: undefined });
          } else {
            if (!(typeof itemObj.country_name === "string" && itemObj.country_name.trim() !== "")) {
              errors.push({ path: "timezone[idx].country_name", expected: "string (non-empty)", value: itemObj.country_name });
            }
          }
          // region_name (required)
          if (itemObj.region_name === undefined) {
            errors.push({ path: "timezone[idx].region_name", expected: "string", value: undefined });
          } else {
            if (!(typeof itemObj.region_name === "string" && itemObj.region_name.trim() !== "")) {
              errors.push({ path: "timezone[idx].region_name", expected: "string (non-empty)", value: itemObj.region_name });
            }
          }
          // time_zone (required)
          if (itemObj.time_zone === undefined) {
            errors.push({ path: "timezone[idx].time_zone", expected: "string", value: undefined });
          } else {
            if (typeof itemObj.time_zone !== "string") {
              errors.push({ path: "timezone[idx].time_zone", expected: "string", value: itemObj.time_zone });
            }
          }
        }
          });
        }
      }
      // ip (required)
      if (objObj.ip === undefined) {
        errors.push({ path: "ip", expected: "string", value: undefined });
      } else {
        if (typeof objObj.ip !== "string") {
          errors.push({ path: "ip", expected: "string", value: objObj.ip });
        }
      }
    }
  
  if (errors.length === 0 && isTimeZoneCheckResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "TimeZoneCheckResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * TimeZoneCheckResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertTimeZoneCheckResponseDto(input: unknown): TimeZoneCheckResponseDto {
  const validation = validateTimeZoneCheckResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertTimeZoneCheckResponseDto] :: 검증 실패: TimeZoneCheckResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
